// src/pages/Committees/Unit.jsx
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Container, Alert, Pagination, Modal, Form,
  Badge, Row, Col, Card, Spinner, InputGroup
} from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaFilter, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { committeeAPI, universityAPI } from '../../api/wbuoaApi';
import { useAuth } from '../../context/AuthContext';

const UnitCommittee = () => {
  // Get auth context
  const { user } = useAuth();

  // State management
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10); // API limit
  const [showModal, setShowModal] = useState(false);
  const [currentMember, setCurrentMember] = useState({
    uc_ID: '',
    university: '',
    name: '',
    designation: '',
    email: '',
    mobile: '',
    portfolio: '',
    member: 0, // MIMBOD flag: 0 = inactive, 1 = active
    show_order: '' // Display order for sorting
  });
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUniversity, setSelectedUniversity] = useState('all');
  const [selectedMimbodStatus, setSelectedMimbodStatus] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'show_order', direction: 'asc' });
  const [dataLoaded, setDataLoaded] = useState(false);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 1,
    has_prev: false,
    has_next: false
  });

  // Extract user data properly from the nested structure
  const userData = user?.data || user;
  const userRole = userData?.role || 'user';
  const userUniversity = userData?.university || '';

  // Check if current user is admin
  const isAdmin = userRole === 'admin';

  // Portfolio options for dropdown
  const portfolioOptions = [
    'President',
    'Secretary',
    'Joint Secretary',
    'Treasurer',
    'Executive Member',
    'Member'
  ];

  // Form label style
  const formLabelStyle = {
    color: '#1a4a2f',
    fontWeight: '500'
  };

  // Fetch members and universities when component mounts
  useEffect(() => {
    if (user) {
      // Initialize with first page
      setCurrentPage(1);
      Promise.all([fetchMembers(1), fetchUniversities()]).then(() => {
        setDataLoaded(true);
      });
    }
  }, [user]);

  // Apply filters when members or filter criteria changes
  useEffect(() => {
    applyFilters();
  }, [members, searchTerm, selectedUniversity, selectedMimbodStatus, sortConfig]);

  // Fetch members with pagination - using the exact API format: {{baseurl}}/unitcommittee?page=3&limit=10
  const fetchMembers = async (page = 1) => {
    setIsLoading(true);
    setError(null);

    try {
      // Call API with pagination parameters - page and limit
      // This will make a request to: /unitcommittee?page={page}&limit={itemsPerPage}
      const response = await committeeAPI.listUnitCommittee(page, itemsPerPage);

      // Handle response structure
      let membersData = [];
      let paginationData = {
        total: 0,
        page: page,
        limit: itemsPerPage,
        total_pages: 1,
        has_prev: page > 1,
        has_next: false
      };

      if (response && response.data) {
        if (Array.isArray(response.data)) {
          // Response format: { data: [...], pagination: {...} }
          membersData = response.data;
          if (response.pagination) {
            paginationData = response.pagination;
          }
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Nested data format: { data: { data: [...], pagination: {...} } }
          membersData = response.data.data;
          if (response.data.pagination) {
            paginationData = response.data.pagination;
          }
        }
      } else if (Array.isArray(response)) {
        // Response is directly an array (no pagination)
        membersData = response;
        paginationData = {
          total: response.length,
          page: 1,
          limit: response.length,
          total_pages: 1,
          has_prev: false,
          has_next: false
        };
      }

      // For non-admin users, filter members by their university
      let filteredMembersData = membersData;
      if (!isAdmin && userUniversity) {
        filteredMembersData = membersData.filter(
          member => member.university === userUniversity
        );
      }

      // Convert show_order to number for proper sorting
      const processedMembers = filteredMembersData.map(member => ({
        ...member,
        show_order: member.show_order ? parseInt(member.show_order) : 999 // Default to 999 if no order set
      }));

      setMembers(processedMembers);

      // Update pagination info
      setPaginationMeta(paginationData);
      setTotalPages(paginationData.total_pages || 1);
      setTotalItems(paginationData.total || processedMembers.length);
      setCurrentPage(page);

    } catch (err) {
      console.error('Error fetching members:', err);
      setError('Failed to fetch committee members: ' + (err.message || 'Server error'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUniversities = async () => {
    setIsLoadingUniversities(true);
    try {
      const response = await universityAPI.listUniversitiesAll();
      // Format universities from API response - using University field from API
      const universitiesData = Array.isArray(response) ? response : (response.data || []);

      // Format universities for dropdown
      const formattedUniversities = universitiesData.map(univ => ({
        id: univ.U_ID,
        name: univ.University || 'Unknown University',
        value: univ.University || 'Unknown University'
      }));

      // Sort universities alphabetically
      formattedUniversities.sort((a, b) => a.name.localeCompare(b.name));

      setUniversities(formattedUniversities);
    } catch (err) {
      console.error('Failed to fetch universities:', err);
      setError('Failed to fetch universities: ' + (err.message || 'Server error'));
    } finally {
      setIsLoadingUniversities(false);
    }
  };

  // Apply filters, sorting (client-side filtering on current page data)
  const applyFilters = () => {
    let filtered = [...members];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(member =>
        member.name?.toLowerCase().includes(term) ||
        member.designation?.toLowerCase().includes(term) ||
        member.email?.toLowerCase().includes(term) ||
        member.mobile?.toLowerCase().includes(term) ||
        member.portfolio?.toLowerCase().includes(term) ||
        member.university?.toLowerCase().includes(term)
      );
    }

    // Apply university filter (for admin only)
    if (isAdmin && selectedUniversity !== 'all') {
      filtered = filtered.filter(member =>
        member.university === selectedUniversity
      );
    }

    // Apply MIMBOD status filter
    if (selectedMimbodStatus !== 'all') {
      const mimbodValue = selectedMimbodStatus === 'active' ? 1 : 0;
      filtered = filtered.filter(member => member.member === mimbodValue);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortConfig.key) {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });

    setFilteredMembers(filtered);
  };

  // Handle page change - fetch new page from API
  const handlePageChange = async (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    await fetchMembers(page);
  };

  // Handle sort request
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  // Validate form function
  const validateForm = () => {
    const errors = {};
    if (!currentMember.name.trim()) errors.name = 'Name is required';
    if (!currentMember.university.trim()) errors.university = 'University is required';
    if (!currentMember.designation.trim()) errors.designation = 'Designation is required';
    if (!currentMember.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentMember.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!currentMember.mobile.trim()) errors.mobile = 'Mobile number is required';
    if (!currentMember.portfolio.trim()) errors.portfolio = 'Portfolio is required';

    // Validate show_order (optional but must be number if provided)
    if (currentMember.show_order && isNaN(currentMember.show_order)) {
      errors.show_order = 'Display order must be a number';
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare payload with show_order
      const payload = {
        ...currentMember,
        // Ensure show_order is sent as string if it exists, or empty string if not
        show_order: currentMember.show_order || ''
      };

      // For non-admin users, ensure university is set correctly
      if (!isAdmin && userUniversity) {
        payload.university = userUniversity;
      }

      if (currentMember.uc_ID) {
        await committeeAPI.updateUnitMember(currentMember.uc_ID, payload);
        setSuccess('Member updated successfully');
      } else {
        await committeeAPI.createUnitMember(payload);
        setSuccess('Member added successfully');
      }

      // Refresh the list - go back to first page
      setCurrentPage(1);
      await fetchMembers(1);
      setShowModal(false);

      // Reset form
      resetForm();

      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error submitting member:', err);
      setError(`Operation failed: ${err.response?.data?.message || err.message || 'Please try again'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form function
  const resetForm = () => {
    setCurrentMember({
      uc_ID: '',
      university: !isAdmin && userUniversity ? userUniversity : '',
      name: '',
      designation: '',
      email: '',
      mobile: '',
      portfolio: '',
      member: 0,
      show_order: ''
    });
  };

  // Handle member deletion
  const handleDelete = async (id) => {
    if (!id) {
      setError('Invalid member ID');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this member? This action cannot be undone.')) {
      return;
    }

    setDeletingId(id);
    setError(null);
    setSuccess(null);

    try {
      await committeeAPI.deleteUnitMember(id);
      setSuccess('Member deleted successfully');

      // Check if this was the last item on the current page
      if (members.length === 1 && currentPage > 1) {
        // Go to previous page
        await fetchMembers(currentPage - 1);
      } else {
        // Refresh the current page
        await fetchMembers(currentPage);
      }

      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting member:', err);
      setError(`Failed to delete member: ${err.message || 'Server error'}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Handle edit button click
  const handleEdit = (member) => {
    if (!member?.uc_ID) {
      setError('Invalid member data');
      return;
    }

    // For non-admin users, check if they can edit this member
    if (!isAdmin && userUniversity && member.university !== userUniversity) {
      setError('You can only edit members from your university');
      return;
    }

    // For non-admin users, ensure university is set to user's university
    const universityValue = !isAdmin && userUniversity ? userUniversity : (member.university || '');

    setCurrentMember({
      uc_ID: member.uc_ID,
      university: universityValue,
      name: member.name || '',
      designation: member.designation || '',
      email: member.email || '',
      mobile: member.mobile || '',
      portfolio: member.portfolio || '',
      member: member.member || 0,
      show_order: member.show_order || ''
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Handle create new member
  const handleCreate = () => {
    resetForm();
    setFormErrors({});
    setShowModal(true);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // For non-admin users, prevent changing university field
    if (!isAdmin && name === 'university') {
      return;
    }

    // For show_order, only allow numbers
    if (name === 'show_order') {
      // Allow empty or numeric values only
      if (value === '' || /^\d*$/.test(value)) {
        setCurrentMember(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setCurrentMember(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Handle MIMBOD toggle
  const handleMemberToggle = () => {
    setCurrentMember(prev => ({
      ...prev,
      member: prev.member === 1 ? 0 : 1
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    if (isAdmin) {
      setSelectedUniversity('all');
    } else {
      setSelectedUniversity('all');
    }
    setSelectedMimbodStatus('all');
    setSortConfig({ key: 'show_order', direction: 'asc' });
  };

  // Generate pagination items
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" />);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  // Get stats (based on total from pagination)
  const getStats = () => {
    return {
      total: paginationMeta.total || 0,
      active: members.filter(m => m.member === 1).length,
      inactive: members.filter(m => m.member === 0).length,
      universities: [...new Set(members.map(m => m.university))].filter(u => u).length
    };
  };

  const stats = getStats();

  if (!user || !dataLoaded) {
    return (
      <Container className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading user information...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header Section */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Unit Committee Management</h2>
              <p className="text-muted mb-0">
                Manage unit committee members and their details. Use display order to control sorting.
              </p>
              {!isAdmin && (
                <Badge bg="info" className="mt-2">
                  Viewing: {userUniversity || 'Your University'}
                </Badge>
              )}
            </div>
            <Button variant="primary" onClick={handleCreate} disabled={isLoading}>
              <FaPlus className="me-2" />
              Add Member
            </Button>
          </div>

          {/* Search and Filter Section */}
          <Row className="g-3 mb-4">
            <Col md={isAdmin ? 5 : 9}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search members by name, designation, email, or university..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            {isAdmin && (
              <Col md={3}>
                <Form.Select
                  value={selectedUniversity}
                  onChange={(e) => {
                    setSelectedUniversity(e.target.value);
                  }}
                >
                  <option value="all">All Universities</option>
                  {universities.map((university) => (
                    <option key={university.id} value={university.value}>
                      {university.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            )}
            <Col md={isAdmin ? 2 : 3}>
              <Form.Select
                value={selectedMimbodStatus}
                onChange={(e) => {
                  setSelectedMimbodStatus(e.target.value);
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active MIMBOD</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            {(searchTerm || selectedUniversity !== 'all' || selectedMimbodStatus !== 'all') && (
              <Col md={2}>
                <Button
                  variant="outline-secondary"
                  onClick={resetFilters}
                  className="w-100"
                  title="Reset Filters"
                >
                  <FaFilter /> Reset
                </Button>
              </Col>
            )}
          </Row>

          {/* Stats Cards */}
          <Row className="mt-4">
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Total Members</h6>
                      <h4 className="mb-0">{stats.total}</h4>
                    </div>
                    <div className="text-primary">
                      <FaPlus size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Active MIMBOD</h6>
                      <h4 className="mb-0">{stats.active}</h4>
                    </div>
                    <div className="text-success">
                      <Badge bg="success">Active</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Inactive</h6>
                      <h4 className="mb-0">{stats.inactive}</h4>
                    </div>
                    <div className="text-secondary">
                      <Badge bg="secondary">Inactive</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Universities</h6>
                      <h4 className="mb-0">{stats.universities}</h4>
                    </div>
                    <div className="text-info">
                      <Badge bg="info">Univ</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-4">
          <Alert.Heading>Success</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      {/* Members Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading committee members...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th
                        style={{ cursor: 'pointer', width: '80px' }}
                        onClick={() => requestSort('show_order')}
                      >
                        <div className="d-flex align-items-center gap-1">
                          Order
                          {getSortIcon('show_order')}
                        </div>
                      </th>
                      <th onClick={() => requestSort('name')} style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-1">
                          Name
                          {getSortIcon('name')}
                        </div>
                      </th>
                      <th onClick={() => requestSort('university')} style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-1">
                          University
                          {getSortIcon('university')}
                        </div>
                      </th>
                      <th onClick={() => requestSort('designation')} style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-1">
                          Designation
                          {getSortIcon('designation')}
                        </div>
                      </th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th onClick={() => requestSort('portfolio')} style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-1">
                          Portfolio
                          {getSortIcon('portfolio')}
                        </div>
                      </th>
                      <th onClick={() => requestSort('member')} style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-1">
                          MIMBOD Status
                          {getSortIcon('member')}
                        </div>
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map(member => (
                        <tr key={member.uc_ID}>
                          <td>
                            <Badge bg="light" text="dark" className="fw-normal">
                              {member.show_order || 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            <div className="fw-semibold">{member.name}</div>
                          </td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '150px' }} title={member.university}>
                              {member.university}
                            </div>
                          </td>
                          <td>{member.designation}</td>
                          <td>
                            <div className="text-truncate" style={{ maxWidth: '180px' }} title={member.email}>
                              {member.email}
                            </div>
                          </td>
                          <td>{member.mobile}</td>
                          <td>{member.portfolio}</td>
                          <td>
                            <Badge
                              bg={member.member === 1 ? 'success' : 'secondary'}
                              className="text-uppercase"
                            >
                              {member.member === 1 ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEdit(member)}
                                disabled={deletingId !== null}
                                title="Edit"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(member.uc_ID)}
                                disabled={deletingId !== null}
                                title="Delete"
                              >
                                {deletingId === member.uc_ID ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <FaTrash />
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center py-5">
                          <div className="py-4">
                            <FaSearch size={48} className="text-muted mb-3" />
                            <h5>No committee members found</h5>
                            <p className="text-muted">
                              {searchTerm || selectedUniversity !== 'all' || selectedMimbodStatus !== 'all'
                                ? 'Try changing your search or filters'
                                : 'Add your first committee member to get started'}
                            </p>
                            {searchTerm || selectedUniversity !== 'all' || selectedMimbodStatus !== 'all' ? (
                              <Button variant="outline-primary" onClick={resetFilters}>
                                Reset Filters
                              </Button>
                            ) : (
                              <Button variant="primary" onClick={handleCreate}>
                                <FaPlus className="me-2" /> Add Member
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination - Always show when totalPages > 1 */}
              {totalPages > 1 && (
                <div className="p-3 border-top">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="text-muted">
                      Showing page {currentPage} of {totalPages} (Total: {totalItems} members)
                    </div>
                    <Pagination className="mb-0">
                      {renderPagination()}
                    </Pagination>
                  </div>
                </div>
              )}

              {/* Show total items info even when no pagination needed */}
              {totalPages <= 1 && totalItems > 0 && (
                <div className="p-3 border-top">
                  <div className="text-muted text-center">
                    Total: {totalItems} member{totalItems !== 1 ? 's' : ''}
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Member Modal */}
      <Modal show={showModal} onHide={() => !isSubmitting && setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{currentMember.uc_ID ? 'Edit Committee Member' : 'Add Committee Member'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>University *</Form.Label>
                  {isLoadingUniversities ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <>
                      {!isAdmin ? (
                        // For non-admin users: show read-only text field with user's university
                        <Form.Control
                          type="text"
                          name="university"
                          value={currentMember.university || userUniversity || ''}
                          readOnly
                          disabled
                          isInvalid={!!formErrors.university}
                        />
                      ) : (
                        // For admin users: show dropdown with all universities
                        <Form.Select
                          name="university"
                          value={currentMember.university}
                          onChange={handleChange}
                          isInvalid={!!formErrors.university}
                          required
                          disabled={isSubmitting}
                        >
                          <option value="">Select University</option>
                          {universities.map((university) => (
                            <option
                              key={university.id}
                              value={university.value}
                            >
                              {university.name}
                            </option>
                          ))}
                        </Form.Select>
                      )}

                      {!isAdmin && (
                        <Form.Text className="text-muted">
                          University is set to your institution: {userUniversity}
                        </Form.Text>
                      )}
                      <Form.Control.Feedback type="invalid">
                        {formErrors.university}
                      </Form.Control.Feedback>
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Display Order</Form.Label>
                  <Form.Control
                    type="text"
                    name="show_order"
                    value={currentMember.show_order}
                    onChange={handleChange}
                    isInvalid={!!formErrors.show_order}
                    placeholder="Enter display order (numbers only)"
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.show_order}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Lower numbers appear first. Members are sorted by this field.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={currentMember.name}
                    onChange={handleChange}
                    isInvalid={!!formErrors.name}
                    placeholder="Enter full name"
                    required
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Designation *</Form.Label>
                  <Form.Control
                    type="text"
                    name="designation"
                    value={currentMember.designation}
                    onChange={handleChange}
                    isInvalid={!!formErrors.designation}
                    placeholder="Enter designation"
                    required
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.designation}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={currentMember.email}
                    onChange={handleChange}
                    isInvalid={!!formErrors.email}
                    placeholder="Enter email address"
                    required
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Mobile *</Form.Label>
                  <Form.Control
                    type="text"
                    name="mobile"
                    value={currentMember.mobile}
                    onChange={handleChange}
                    isInvalid={!!formErrors.mobile}
                    placeholder="Enter mobile number"
                    required
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.mobile}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>Portfolio *</Form.Label>
                  <Form.Select
                    name="portfolio"
                    value={currentMember.portfolio}
                    onChange={handleChange}
                    isInvalid={!!formErrors.portfolio}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Portfolio</option>
                    {portfolioOptions.map((portfolio, index) => (
                      <option key={index} value={portfolio}>
                        {portfolio}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.portfolio}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={formLabelStyle}>MIMBOD Status</Form.Label>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="member-toggle"
                        checked={currentMember.member === 1}
                        onChange={handleMemberToggle}
                        style={{ transform: 'scale(1.5)', marginRight: '10px' }}
                        disabled={isSubmitting}
                      />
                      <label className="form-check-label" htmlFor="member-toggle">
                        {currentMember.member === 1 ? 'Active Member' : 'Inactive'}
                      </label>
                    </div>
                    <Badge bg={currentMember.member === 1 ? "success" : "secondary"} className="text-uppercase">
                      {currentMember.member === 1 ? 'Active MIMBOD' : 'Inactive'}
                    </Badge>
                  </div>
                  <Form.Text className="text-muted">
                    Toggle to mark member as active/inactive for MIMBOD
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {currentMember.uc_ID ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  currentMember.uc_ID ? 'Update Member' : 'Create Member'
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default UnitCommittee;